import { makeAutoObservable, reaction, runInAction } from "mobx";
import posthog from "posthog-js";
import UserService from "./UserService";

export interface StravaActivity {
    id: number;
    name: string;
    distance: number;
    moving_time: number;
    elapsed_time: number;
    total_elevation_gain: number;
    type: string;
    start_date: string;
    start_date_local: string;
    timezone: string;
    start_latlng: [number, number] | null;
    end_latlng: [number, number] | null;
    map: {
        id: string;
        summary_polyline: string;
        polyline?: string;
    };
    average_speed: number;
    max_speed: number;
    average_heartrate?: number;
    max_heartrate?: number;
    elev_high?: number;
    elev_low?: number;
}

export interface StravaTokenResponse {
    token_type: string;
    access_token: string;
    expires_at: number;
    expires_in: number;
    refresh_token: string;
}

export interface StravaAthlete {
    id: number;
    username: string;
    firstname: string;
    lastname: string;
    profile_medium: string;
}

export interface StravaCredentials {
    athleteId: number;
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
    athlete?: StravaAthlete;
}

export interface StravaActivityStreams {
  time: number[];
  distance: number[];
  altitude?: number[];
  velocity_smooth?: number[];
  heartrate?: number[];
  cadence?: number[];
  watts?: number[];
  temp?: number[];
  moving?: boolean[];
  grade_smooth?: number[];
  latlng?: [number, number][];
}

export default class StravaService {
    private _userService: UserService;
    private _isConnected: boolean = false;
    private _isLoading: boolean = false;
    private _credentials: StravaCredentials | null = null;
    private _activities: StravaActivity[] = [];
    private _error: string | null = null;

    private readonly _clientId: string = process.env.REACT_APP_STRAVA_CLIENT_ID || '';
    private readonly _clientSecret: string = process.env.REACT_APP_STRAVA_CLIENT_SECRET || '';
    private readonly _stravaAuthUrl: string = 'https://www.strava.com/oauth/authorize';
    private readonly _stravaTokenUrl: string = 'https://www.strava.com/oauth/token';
    private readonly _stravaApiUrl: string = 'https://www.strava.com/api/v3';

    constructor(userService: UserService) {
        this._userService = userService;
        makeAutoObservable(this);
        this._initializeFromStorage();

        reaction(
            () => this._userService.user,
            (user) => {
                if (!user) {
                    runInAction(() => {
                        this._isConnected = false;
                        this._credentials = null;
                        this._activities = [];
                    });
                } else {
                    this._initializeFromStorage();
                }
            }
        );
    }

    get isConnected(): boolean {
        return this._isConnected && !!this._credentials;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get activities(): StravaActivity[] {
        return this._activities;
    }

    get error(): string | null {
        return this._error;
    }

    get athlete(): StravaAthlete | undefined {
        return this._credentials?.athlete;
    }

    private async _initializeFromStorage(): Promise<void> {
        this._isLoading = true;

        try {

            if (this._userService.user?.stravaCredentials?.athlete) {
                const credentials = this._userService.user.stravaCredentials;

                if (credentials.expiresAt * 1000 < Date.now()) {
                    await this._refreshToken(credentials.refreshToken);
                } else {
                    runInAction(() => {
                        this._credentials = credentials;
                        this._isConnected = true;
                    });
                }
            }
        } catch (error) {
            console.error('Error initializing Strava service:', error);
            this._error = 'Failed to initialize Strava connection';
            posthog.capture('error_strava_init', { error: (error as Error).message });
        } finally {
            this._isLoading = false;
        }
    }

    getAuthorizationUrl(): string {
        const scope = 'activity:read_all';

        const params = new URLSearchParams({
            client_id: this._clientId,
            redirect_uri: window.location.href.split('?')[0],
            response_type: 'code',
            approval_prompt: 'auto',
            scope
        });

        return `${this._stravaAuthUrl}?${params.toString()}`;
    }

    connect(): void {
        this._isLoading = true;
        this._error = null;

        try {
            const authUrl = this.getAuthorizationUrl();
            window.location.href = authUrl;
            posthog.capture('strava_connect_initiated');
        } catch (error) {
            console.error('Error connecting to Strava:', error);
            this._error = 'Failed to start Strava authentication';
            this._isLoading = false;
            posthog.capture('error_strava_connect', { error: (error as Error).message });
        }
    }

    async handleCallback(code: string): Promise<boolean> {
        if (this._isConnected) {
            return true;
        }
        this._isLoading = true;
        this._error = null;

        try {
            const response = await fetch(this._stravaTokenUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    client_id: this._clientId,
                    client_secret: this._clientSecret,
                    code,
                    grant_type: 'authorization_code'
                })
            });

            if (!response.ok) {
                throw new Error(`Strava API error: ${response.status}`);
            }

            const data = await response.json();

            const credentials: StravaCredentials = {
                athleteId: data.athlete.id,
                accessToken: data.access_token,
                refreshToken: data.refresh_token,
                expiresAt: data.expires_at,
                athlete: data.athlete
            };

            await this._saveCredentials(credentials);

            runInAction(() => {
                this._credentials = credentials;
                this._isConnected = true;
            });

            posthog.capture('strava_connected', { athlete_id: data.athlete.id });
            return true;
        } catch (error) {
            console.error('Error handling Strava callback:', error);
            this._error = 'Failed to complete Strava authentication';
            posthog.capture('error_strava_callback', { error: (error as Error).message });
            return false;
        } finally {
            this._isLoading = false;
        }
    }

    private async _refreshToken(refreshToken: string): Promise<void> {
        try {
            const response = await fetch(this._stravaTokenUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    client_id: this._clientId,
                    client_secret: this._clientSecret,
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token'
                })
            });

            if (!response.ok) {
                throw new Error(`Strava API error: ${response.status}`);
            }

            const data = await response.json();

            const credentials: StravaCredentials = {
                athleteId: this._credentials?.athleteId || this._userService.user?.stravaCredentials?.athleteId || data.athlete.id,
                accessToken: data.access_token,
                refreshToken: data.refresh_token,
                expiresAt: data.expires_at,
                athlete: this._credentials?.athlete || this._userService.user?.stravaCredentials?.athlete || data.athlete
            };

            await this._saveCredentials(credentials);

            runInAction(() => {
                this._credentials = credentials;
                this._isConnected = true;
            });
        } catch (error) {
            console.error('Error refreshing Strava token:', error);
            this._error = 'Failed to refresh Strava token';
            this._isConnected = false;
            posthog.capture('error_strava_refresh_token', { error: (error as Error).message });
        }
    }

    private async _saveCredentials(credentials: StravaCredentials | null): Promise<void> {

        if (!this._userService.user) {
            throw new Error('User not logged in');
        }

        this._userService.updateUserData({
            ...this._userService.user,
            stravaCredentials: credentials || {
                athleteId: 0,
                accessToken: '',
                refreshToken: '',
                expiresAt: 0
            }
        });
    }

    private async _makeStravaRequest<T>(endpoint: string, method: string = 'GET', params?: Record<string, string>): Promise<T> {
        if (!this._credentials) {
            throw new Error('Not connected to Strava');
        }

        if (this._credentials.expiresAt * 1000 < Date.now()) {
            await this._refreshToken(this._credentials.refreshToken);
        }

        let url = `${this._stravaApiUrl}${endpoint}`;

        if (params && method === 'GET') {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                queryParams.append(key, value);
            });
            url += `?${queryParams.toString()}`;
        }

        const response = await fetch(url, {
            method,
            headers: {
                'Authorization': `Bearer ${this._credentials.accessToken}`,
                'Content-Type': 'application/json'
            },
            body: method !== 'GET' && params ? JSON.stringify(params) : undefined
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Strava API error: ${response.status} - ${errorText}`);
        }

        return await response.json() as T;
    }

    async disconnect(): Promise<void> {
        this._isLoading = true;

        try {
            if (this._credentials) {
                try {
                    await fetch(`${this._stravaApiUrl}/oauth/deauthorize`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${this._credentials.accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });
                } catch (error) {
                    console.warn('Error deauthorizing from Strava:', error);
                }
            }

            await this._saveCredentials(null);

            runInAction(() => {
                this._credentials = null;
                this._isConnected = false;
                this._activities = [];
            });

            posthog.capture('strava_disconnected');
        } catch (error) {
            console.error('Error disconnecting from Strava:', error);
            this._error = 'Failed to disconnect from Strava';
            posthog.capture('error_strava_disconnect', { error: (error as Error).message });
        } finally {
            this._isLoading = false;
        }
    }

    async getActivities(after?: number, before?: number, limit: number = 30): Promise<StravaActivity[]> {
        this._isLoading = true;
        this._error = null;

        try {
            const params: Record<string, string> = {
                per_page: limit.toString()
            };

            if (after) {
                params.after = after.toString();
            }

            if (before) {
                params.before = before.toString();
            }

            const activities = await this._makeStravaRequest<StravaActivity[]>('/athlete/activities', 'GET', params);

            runInAction(() => {
                this._activities = activities;
            });

            posthog.capture('strava_activities_fetched', {
                count: activities.length,
                has_after: !!after,
                has_before: !!before
            });

            return activities;
        } catch (error) {
            console.error('Error fetching Strava activities:', error);
            this._error = 'Failed to fetch activities from Strava';
            posthog.capture('error_strava_get_activities', { error: (error as Error).message });
            return [];
        } finally {
            this._isLoading = false;
        }
    }

    async getActivityDetail(activityId: number): Promise<StravaActivity | null> {
        this._isLoading = true;
        this._error = null;

        try {
            const activity = await this._makeStravaRequest<StravaActivity>(`/activities/${activityId}`);
            posthog.capture('strava_activity_detail_fetched', { activity_id: activityId });
            return activity;
        } catch (error) {
            console.error(`Error fetching Strava activity ${activityId}:`, error);
            this._error = 'Failed to fetch activity details from Strava';
            posthog.capture('error_strava_get_activity_detail', {
                error: (error as Error).message,
                activity_id: activityId
            });
            return null;
        } finally {
            this._isLoading = false;
        }
    }

    async getActivitiesByDateRange(startDate: Date, endDate: Date, limit: number = 30): Promise<StravaActivity[]> {
        const after = Math.floor(startDate.getTime() / 1000);
        const before = Math.floor(endDate.getTime() / 1000);
        return this.getActivities(after, before, limit);
    }

    async getActivitiesByType(type: string, startDate?: Date, endDate?: Date, limit: number = 30): Promise<StravaActivity[]> {
        let after: number | undefined;
        let before: number | undefined;

        if (startDate) {
            after = Math.floor(startDate.getTime() / 1000);
        }

        if (endDate) {
            before = Math.floor(endDate.getTime() / 1000);
        }

        const activities = await this.getActivities(after, before, limit);
        return activities.filter(activity => activity.type === type);
    }

    async getActivityStreams(
      activityId: number, 
      streamTypes: string[] = ['time', 'distance', 'latlng', 'altitude', 'velocity_smooth', "moving"]
    ): Promise<StravaActivityStreams | null> {
      this._isLoading = true;
      this._error = null;
      
      try {
        const streamTypesString = streamTypes.join(',');
        const endpoint = `/activities/${activityId}/streams?keys=${streamTypesString}&key_by_type=true`;
        
        const streamsData = await this._makeStravaRequest<any>(endpoint);
        
        const formattedStreams: StravaActivityStreams = {
          time: streamsData.time?.data || [],
          distance: streamsData.distance?.data || [],
        };
        
        if (streamsData.latlng?.data) {
          formattedStreams.latlng = streamsData.latlng.data;
        }
        
        if (streamsData.altitude?.data) {
          formattedStreams.altitude = streamsData.altitude.data;
        }
        
        if (streamsData.velocity_smooth?.data) {
          formattedStreams.velocity_smooth = streamsData.velocity_smooth.data;
        }
        
        if (streamsData.heartrate?.data) {
          formattedStreams.heartrate = streamsData.heartrate.data;
        }
        
        if (streamsData.cadence?.data) {
          formattedStreams.cadence = streamsData.cadence.data;
        }
        
        if (streamsData.watts?.data) {
          formattedStreams.watts = streamsData.watts.data;
        }
        
        if (streamsData.temp?.data) {
          formattedStreams.temp = streamsData.temp.data;
        }
        
        if (streamsData.moving?.data) {
          formattedStreams.moving = streamsData.moving.data;
        }
        
        if (streamsData.grade_smooth?.data) {
          formattedStreams.grade_smooth = streamsData.grade_smooth.data;
        }
        
        posthog.capture('strava_activity_streams_fetched', { 
          activity_id: activityId,
          stream_types: streamTypes
        });
        
        runInAction(() => {
          this._isLoading = false;
        });
        
        return formattedStreams;
      } catch (error) {
        console.error(`Error fetching Strava activity streams for ${activityId}:`, error);
        runInAction(() => {
          this._error = 'Failed to fetch activity streams from Strava';
          this._isLoading = false;
        });
        posthog.capture('error', { 
          source: 'getActivityStreams', 
          error: (error as Error).message,
          activity_id: activityId
        });
        return null;
      }
    }
}